import React from 'react';
import { RichTextField } from 'react-admin';
import { Box, MenuItem, Paper, Select, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { LOCALE_NAMES, NEWS_LOCALES, POPUP_BODY_MAX_LENGTH, SOURCE_LOCALE, formatDate, pickLocalized, truncateHtml } from '../common';
import { filledButtonSx, outlinedButtonSx } from '../buttons';

/**
 * Vista previa de una novedad tal y como la verá el usuario del ATS.
 *
 * OJO: es una IMITACIÓN maquetada con MUI del pop-up de hrbf_freezer y de su entrada en la página
 * «Novedades», siguiendo el prototipo validado de la tarea. cooler no comparte componentes con
 * freezer: si cambia el pop-up o el feed del ATS, hay que sincronizar esta vista a mano.
 *
 * El HTML se pinta con `RichTextField`, que lo limpia con DOMPurify antes de inyectarlo.
 */

/**
 * Ancho único de la vista previa: el del modal del ATS (520 px como máximo). Lo comparten la
 * cabecera, la tarjeta del pop-up, la del feed y el pie, para que el selector termine justo en el
 * borde derecho de la tarjeta en los dos modos. Si la columna es más estrecha, encogen juntos.
 */
export const PREVIEW_MAX_WIDTH = 520;
const previewWidthSx = { width: '100%', maxWidth: PREVIEW_MAX_WIDTH, boxSizing: 'border-box' };

const DEFAULT_TITLE = 'Título de la novedad';
const DEFAULT_BODY = '<p>Aquí aparecerá el texto que escribas en el formulario.</p>';

const imageIcon = (size) => <ImageOutlinedIcon sx={{ fontSize: size }} />;

/** Imagen o marcador «Imagen de la novedad». `height` fijo (pop-up) o `aspectRatio` (feed). */
const PreviewImage = ({ src, sx, iconSize = 40 }) =>
    src ? (
        <Box component="img" src={src} alt="" sx={{ display: 'block', width: '100%', objectFit: 'cover', ...sx }} />
    ) : (
        <Box
            data-testid="news-preview-image-placeholder"
            sx={(theme) => ({
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                fontSize: '13px',
                ...sx,
            })}
        >
            {imageIcon(iconSize)}
            Imagen de la novedad
        </Box>
    );

const PreviewBody = ({ html, lineHeight = 1.55, fontSize = '14px' }) => (
    <RichTextField
        record={{ body: html }}
        source="body"
        sx={{
            display: 'block',
            color: 'text.secondary',
            fontSize,
            lineHeight,
            wordBreak: 'break-word',
            '& p': { m: 0, mb: 1 },
            '& p:last-child': { mb: 0 },
            '& ul, & ol': { m: 0, mb: 1, pl: 3 },
            '& a': { color: 'primary.main' },
        }}
    />
);

const Eyebrow = () => (
    <Typography
        sx={{ fontSize: '12px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'primary.main' }}
    >
        Novedad en Velora
    </Typography>
);

// Botones de mentira: se pintan con las medidas del `ActionButton` del ATS (las mismas de
// `buttons.js`: 40 px de alto, radio de 8 px, peso 400), pero no hacen nada. El radio va en px:
// `borderRadius: 2` se multiplica por el radio del tema de cooler (10) y los dejaba en píldora.
const FakeButton = ({ primary, children }) => (
    <Box
        component="span"
        sx={(theme) => ({
            ...(primary ? filledButtonSx : outlinedButtonSx(theme)),
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            boxSizing: 'border-box',
            ...(primary && { bgcolor: 'primary.main', color: 'primary.contrastText' }),
        })}
    >
        {children}
    </Box>
);

/**
 * Pop-up de la vista de usuario del prototipo: modal estrecho (520 px como máximo), imagen de
 * 220 px, botón de cierre redondo arriba a la derecha, antetítulo, título de 21 px, texto y botones.
 */
const PopupMock = ({ title, body, imageSrc }) => (
    <Paper
        elevation={2}
        data-testid="news-preview-popup"
        sx={{ ...previewWidthSx, position: 'relative', borderRadius: '12px', overflow: 'hidden' }}
    >
        <Box
            component="span"
            aria-hidden="true"
            data-testid="news-preview-close"
            sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: 'background.paper',
                color: 'text.secondary',
                boxShadow: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <CloseIcon sx={{ fontSize: 18 }} />
        </Box>
        <PreviewImage src={imageSrc} sx={{ height: 220 }} />
        <Box sx={{ p: '20px 24px 24px', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Eyebrow />
            <Typography component="h2" sx={{ m: 0, fontSize: '21px', fontWeight: 600, lineHeight: 1.3, wordBreak: 'break-word' }}>
                {title}
            </Typography>
            {/* El pop-up enseña solo los primeros caracteres; la novedad completa se lee en el feed. */}
            <PreviewBody html={truncateHtml(body, POPUP_BODY_MAX_LENGTH)} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
                <FakeButton primary>
                    Ver todas las novedades
                    <ArrowForwardIcon sx={{ fontSize: 16 }} />
                </FakeButton>
                <FakeButton>Cerrar</FakeButton>
            </Box>
        </Box>
    </Paper>
);

// Colores de la página «Novedades» de freezer (tokens.ts): fondo azulado, línea de tiempo y «Nuevo».
const FEED = {
    background: '#F4F6FA',
    line: '#d1d5db',
    dot: '#0176BC',
    dotRing: '#E6F3FC',
    newBg: '#E1FAEB',
    newText: '#025832',
    shadow: '1px 1px 5px 0px #00000040',
};

// Mismas reglas que `utils/newsDates.ts` de freezer: fecha larga, día y mes de la línea de tiempo,
// y «Nuevo» durante 15 días desde el inicio (hoy incluido).
const NEW_BADGE_DAYS = 15;
const LONG_MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const SHORT_MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

const splitDate = (iso) => {
    if (!iso) return null;
    const [year, month, day] = iso.slice(0, 10).split('-').map(Number);
    return year && month && day ? { year, month, day } : null;
};

export const isRecentStart = (iso, now = new Date()) => {
    const date = splitDate(iso);
    if (!date) return false;
    const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
    const elapsed = (today - Date.UTC(date.year, date.month - 1, date.day)) / 86400000;
    return elapsed >= 0 && elapsed < NEW_BADGE_DAYS;
};

/**
 * Entrada de la página «Novedades» como la pinta freezer (opción «C + D»): fondo azulado, mes con su
 * punto, día grande a la izquierda unido por la línea de tiempo, y la tarjeta blanca con la imagen
 * arriba (como freezer en pantallas estrechas) y debajo «Nuevo», fecha larga, título y texto. Aquí a
 * escala: la columna de la vista previa mide 520 px como mucho.
 */
const FeedEntryMock = ({ title, body, imageSrc, startsAt }) => {
    const date = splitDate(startsAt);
    const isNew = isRecentStart(startsAt);

    return (
        <Box
            data-testid="news-preview-feed"
            sx={{ ...previewWidthSx, p: 2, borderRadius: '12px', bgcolor: FEED.background, display: 'flex', flexDirection: 'column', gap: 1.5 }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box
                    aria-hidden="true"
                    sx={{ ml: '45px', width: 10, height: 10, borderRadius: 999, bgcolor: FEED.dot, boxShadow: `0 0 0 3px ${FEED.dotRing}` }}
                />
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.secondary' }}>
                    {date ? `${LONG_MONTHS[date.month - 1][0].toUpperCase()}${LONG_MONTHS[date.month - 1].slice(1)} de ${date.year}` : 'Mes de inicio'}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'stretch' }}>
                <Box aria-hidden="true" sx={{ flex: '0 0 36px', display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 1.5 }}>
                    <Box component="span" sx={{ fontSize: '20px', fontWeight: 600, lineHeight: 1 }}>
                        {date ? date.day : '—'}
                    </Box>
                    <Box component="span" sx={{ fontSize: '10px', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        {date ? SHORT_MONTHS[date.month - 1] : ''}
                    </Box>
                </Box>
                <Box aria-hidden="true" sx={{ flex: '0 0 2px', borderRadius: '2px', bgcolor: FEED.line }} />
                <Box
                    component="article"
                    sx={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5, p: 1.5, borderRadius: '12px', bgcolor: 'background.paper', boxShadow: FEED.shadow }}
                >
                    {/* Imagen arriba a todo el ancho de la tarjeta: es como la pinta freezer en
                        pantallas estrechas. A 520 px la tarjeta horizontal dejaba el texto en una
                        columna alta y la imagen demasiado pequeña para revisarla. La caja propia evita
                        que un <img> como hijo de flex desborde por su ancho natural. */}
                    <Box data-testid="news-preview-feed-image" sx={{ width: '100%', minWidth: 0 }}>
                        <PreviewImage
                            src={imageSrc}
                            iconSize={22}
                            sx={{ width: '100%', aspectRatio: '16 / 9', borderRadius: '8px', fontSize: '10px', gap: 0.5, textAlign: 'center' }}
                        />
                    </Box>
                    <Box sx={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
                            {isNew && (
                                <Box
                                    component="span"
                                    sx={{ px: 1, py: '2px', borderRadius: 999, bgcolor: FEED.newBg, color: FEED.newText, fontSize: '11px', fontWeight: 500 }}
                                >
                                    Nuevo
                                </Box>
                            )}
                            <Typography component="time" dateTime={startsAt || undefined} color="text.secondary" sx={{ fontSize: '12px' }}>
                                {date ? `${date.day} de ${LONG_MONTHS[date.month - 1]} de ${date.year}` : formatDate(startsAt)}
                            </Typography>
                        </Box>
                        <Typography component="h2" sx={{ m: 0, fontSize: '15px', fontWeight: 600, lineHeight: 1.35, wordBreak: 'break-word' }}>
                            {title}
                        </Typography>
                        <PreviewBody html={body} fontSize="13px" />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

/**
 * @param {object} props
 * @param {Record<string,string>} props.title  Título por idioma.
 * @param {Record<string,string>} props.body   Texto HTML por idioma.
 * @param {string} props.locale               Idioma que se quiere ver; si está vacío, cae al español.
 * @param {string|null} props.imageSrc
 * @param {string|null} props.startsAt
 * @param {string|null} props.endsAt
 */
/**
 * Con `onLocaleChange`, el idioma de la cabecera es un desplegable («Vista previa · Español ▾»): así
 * la vista detalle no necesita una fila de botones de idioma aparte encima del selector de modo.
 */
const NewsPopupPreview = ({ title, body, locale = SOURCE_LOCALE, onLocaleChange, imageSrc, startsAt, endsAt, defaultMode = 'popup' }) => {
    const [mode, setMode] = React.useState(defaultMode);
    const shownTitle = pickLocalized(title, locale);
    const shownBody = pickLocalized(body, locale);
    const fallback = shownTitle.fallback || shownBody.fallback;
    const props = {
        title: shownTitle.value || DEFAULT_TITLE,
        body: shownBody.value || DEFAULT_BODY,
        imageSrc,
        startsAt,
    };

    return (
        <Box sx={{ ...previewWidthSx, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Siempre en una fila: la etiqueta no cambia con el modo (ya lo dice el selector) y, si no
                cabe, se corta con puntos suspensivos; el selector no encoge ni salta de línea. */}
            <Box
                data-testid="news-preview-header"
                sx={{ ...previewWidthSx, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'nowrap' }}
            >
                <Typography
                    title={`Vista previa · ${LOCALE_NAMES[locale] || locale}`}
                    sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        color: 'text.secondary',
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {onLocaleChange ? (
                        <>
                            Vista previa ·{' '}
                            <Select
                                variant="standard"
                                disableUnderline
                                value={locale}
                                onChange={(event) => onLocaleChange(event.target.value)}
                                inputProps={{ 'aria-label': 'Idioma de la vista previa' }}
                                sx={{
                                    fontSize: 'inherit',
                                    fontWeight: 'inherit',
                                    letterSpacing: 'inherit',
                                    color: 'primary.main',
                                    '& .MuiSelect-select': { py: 0, textTransform: 'uppercase' },
                                }}
                            >
                                {NEWS_LOCALES.map((code) => (
                                    <MenuItem key={code} value={code} sx={{ fontSize: 13 }}>
                                        {LOCALE_NAMES[code]}
                                    </MenuItem>
                                ))}
                            </Select>
                        </>
                    ) : (
                        <>Vista previa · {LOCALE_NAMES[locale] || locale}</>
                    )}
                </Typography>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={mode}
                    onChange={(event, value) => value && setMode(value)}
                    aria-label="Tipo de vista previa"
                    sx={{ flexShrink: 0 }}
                >
                    <ToggleButton value="popup">Pop-up</ToggleButton>
                    <ToggleButton value="feed">Entrada del feed</ToggleButton>
                </ToggleButtonGroup>
            </Box>
            {fallback && (
                <Typography color="warning.main" data-testid="news-preview-fallback" sx={{ fontSize: '12px' }}>
                    Sin contenido en {LOCALE_NAMES[locale]?.toLowerCase() || locale}: el usuario lo verá en español.
                </Typography>
            )}
            {mode === 'popup' ? <PopupMock {...props} /> : <FeedEntryMock {...props} />}
            {mode === 'popup' ? (
                <Typography color="text.secondary" data-testid="news-preview-footer" sx={{ ...previewWidthSx, fontSize: '13px' }}>
                    Se mostrará del {formatDate(startsAt)} al {formatDate(endsAt)}.
                </Typography>
            ) : (
                // La tarjeta del feed se ve aquí con la imagen arriba, como en pantallas estrechas.
                <Typography color="text.secondary" data-testid="news-preview-feed-note" sx={{ ...previewWidthSx, fontSize: '13px' }}>
                    En escritorio, la imagen se muestra a la izquierda del texto.
                </Typography>
            )}
        </Box>
    );
};

export default NewsPopupPreview;
